import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EntityRowContainer } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/CreateAdjustmentModal/EntityRowContainer';
import { PaidTrafficSummaryMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/Components/PaidTrafficSummaryMetrics';
import { PROMOTION_OPTIONS } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/Components/RetailPricePromotionInputs';
import {
  PlanTypeOption,
  planTypeToDisplayName
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/constants';
import { useAdjustmentPaidTrafficMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/hooks/useAdjustmentPaidTrafficMetrics';
import { useNewProjection } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/hooks/useNewProjection';
import { useOriginalProjection } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/hooks/useOriginalProjection';
import { parseOriginalProjectionResponse } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/parsers';
import { AdjustmentForm } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/types';
import {
  generateNetImpactText,
  generateNewProjectionText,
  generateOriginalPlanText
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/RefactoredAdjustmentModals/utils';
import { AdjustmentRecordItem } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/ViewAdjustmentModal/AdjustmentRecordItem';
import { useFetchAdjustmentMetaData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/ViewAdjustmentModal/utils';
import { ForecastsAdjustmentData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/forecastsAdjustmentsTableUtils';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import AdvancedSearchParser from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/AdvancedSearchParser';
import { useMetricFormatter } from 'src/utils/Hooks';
import { shouldShowPromotionsAdjustment } from 'src/utils/app';

/**
 * Returns the default view after clicking on an adjustment. Renders a variety of summarized metrics including a summary of the adjustment plan
 * as well as records of changes that have been made to the adjustment over time.
 */
export const ViewAdjustment = ({
  product,
  adjustmentForm,
  adjustmentData
}: {
  product: ForecastsAdjustmentData['product'];
  adjustmentForm: AdjustmentForm;
  adjustmentData: ForecastsAdjustmentData;
}) => {
  /**
   * Only fetch ad spend once to display in the summarized plan text
   */

  const [showMore, setShowMore] = useState(false);

  const { title, planType, description, startDate, endDate, isPromotionAdjustment, promotionType } = adjustmentForm;
  const formatMetric = useMetricFormatter();
  const fetchAdjustmentMetaData = useFetchAdjustmentMetaData();

  // Used for getting the original metrics for the original plan text.
  const { data: originalProjectionResponse, isLoading: originalProjectionLoading } = useOriginalProjection({
    stacklineSku: product.stacklineSku,
    startWeekId: startDate,
    endWeekId: endDate
  });

  // Fetch published adjusted other traffic for promotion adjustments
  const { data: newProjectionData } = useNewProjection({
    stacklineSku: product.stacklineSku,
    adjustedFields: [['Other Traffic Delta', 'otherTrafficDelta', 'sum']],
    unadjustedFields: [['Other Traffic', 'otherTraffic', 'sum']],
    startWeekId: startDate,
    endWeekId: endDate,
    enabled: adjustmentForm.isPromotionAdjustment
  });

  const parser = new AdvancedSearchParser(newProjectionData);
  // Only use for promotion adjustments
  const otherTrafficForPromotion = parser.parseAggregations((aggregations) => {
    const [delta, original] = aggregations?.by_retailerId ?? [];
    return (delta?.value ?? 0) + (original?.value ?? 0);
  });

  const parsedData = originalProjectionResponse ? parseOriginalProjectionResponse(originalProjectionResponse) : null;

  const { data, isLoading } = useQuery({
    queryKey: [adjustmentData.adjustmentId],
    queryFn: async () =>
      fetchAdjustmentMetaData({
        adjustmentId: adjustmentData.adjustmentId,
        retailerSku: adjustmentData.retailerSku,
        retailerId: adjustmentData.retailerId
      }),
    staleTime: 0,
    retry: 0,
    refetchOnWindowFocus: false
  });

  const { data: paidTrafficData, isLoading: paidTrafficDataLoading } = useAdjustmentPaidTrafficMetrics(adjustmentData);

  const originalProjectionText = useMemo(() => {
    return generateOriginalPlanText({ adjustmentForm, parsedData, formatMetric, isPromotionAdjustment });
  }, [adjustmentForm, parsedData, formatMetric, isPromotionAdjustment]);

  const newProjectionText = useMemo(() => {
    return generateNewProjectionText({
      adjustmentForm,
      parsedData,
      adjustmentData,
      formatMetric,
      paidTrafficMetrics: paidTrafficData,
      otherTrafficForPromotion,
      isPromotionAdjustment
    });
  }, [
    adjustmentData,
    adjustmentForm,
    parsedData,
    formatMetric,
    paidTrafficData,
    isPromotionAdjustment,
    otherTrafficForPromotion
  ]);

  const netImpactText = useMemo(() => {
    return generateNetImpactText({ netImpact: adjustmentData.netImpact, planType, formatMetric });
  }, [adjustmentData.netImpact, planType, formatMetric]);

  return (
    <>
      <EntityRowContainer productEntity={product} />
      <Flex gap="xxl">
        <Flex flexDirection="column" gap="sm" width="100px">
          <Text variant="subtitle2" title="Plan Type">
            Plan Type
          </Text>
          <Text variant="body2">{planTypeToDisplayName[planType]}</Text>
        </Flex>
        <Flex flexDirection="column" gap="sm" width="135px" sx={{ wordBreak: 'break-word' }}>
          <Text variant="subtitle2">Title</Text>
          <Text variant="body2">{title}</Text>
        </Flex>
        <Flex flexDirection="column" gap="sm" width="255px" sx={{ wordBreak: 'break-word' }}>
          <Text variant="subtitle2">Description</Text>
          <Text variant="body2">{description}</Text>
        </Flex>
      </Flex>
      <Flex marginTop="24px" gap="sm" flexDirection="column">
        <Text variant="subtitle2">Plan Change Confirmation</Text>
        <Flex gap="sm" flexDirection="column" minHeight="100px">
          {originalProjectionLoading || paidTrafficDataLoading ? (
            <>
              <SlSkeleton variant="rounded" height={19} width="100%" />
              <SlSkeleton variant="rounded" height={19} width="100%" />
            </>
          ) : (
            <>
              <Text variant="body2">{originalProjectionText}</Text>
              <Text variant="body2">{newProjectionText}</Text>
            </>
          )}
        </Flex>
      </Flex>
      <Flex height="60px" gap="sm" flexDirection="column">
        <Text variant="subtitle2">
          {isPromotionAdjustment
            ? `This price adjustment is a ${
                PROMOTION_OPTIONS.find((option) => promotionType === option.id)?.label ?? ''
              } promotion.`
            : planType === PlanTypeOption.RetailPrice && shouldShowPromotionsAdjustment()
            ? 'This price adjustment is not a promotion.'
            : 'Sales Net Impact'}
        </Text>
        {originalProjectionLoading || paidTrafficDataLoading ? (
          <>
            <SlSkeleton variant="rounded" height={19} width="100%" />
            <SlSkeleton variant="rounded" height={19} width="100%" />
          </>
        ) : (
          <Text variant="body2">{netImpactText}</Text>
        )}
      </Flex>

      {/* Paid Traffic Summary Metrics (only for paid traffic adjustment) */}
      {adjustmentData.planType === PlanTypeOption.PaidTraffic && (
        <PaidTrafficSummaryMetrics adjustmentData={adjustmentData} parsedData={parsedData} />
      )}
      <Flex alignItems="flex-start" marginTop="24px" marginBottom="32px" flexDirection="column">
        {data && !isLoading && !originalProjectionLoading && !paidTrafficDataLoading
          ? data.adjustmentLogs.slice(0, showMore ? undefined : 1).map((record, index) => {
              return (
                <AdjustmentRecordItem
                  adjustmentRecord={record}
                  userData={data.userMetaData[record.updatedByUserId]} // Access the user meta data based on the record user ID
                  key={record.lastUpdatedDate}
                  isFirst={index === 0}
                  isLast={showMore ? index === data.adjustmentLogs.length - 1 : true}
                />
              );
            })
          : null}

        {/* Show more button  */}
        {data && data.adjustmentLogs.length > 1 && !showMore && !originalProjectionLoading ? (
          <button
            style={{ marginLeft: '28px', marginTop: '8px', border: 'none', background: 'none', padding: '0' }}
            onClick={() => setShowMore(true)}
          >
            <Text variant="subtitle2" underlined>
              Show more
              {showMore}
            </Text>
          </button>
        ) : null}
      </Flex>
    </>
  );
};
